// Connectivity Intelligence — editorial reading treatment, Fieldbook. Standalone.
import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./connectivity.css";

export default function ConnectivityPage() {
  return (
    <div className="ffly ffly--public connectivity-page">
      <PageMeta
        title="Connectivity Intelligence"
        description="FHIRfly Connectivity Intelligence helps developers discover healthcare integration endpoints from NPPES, CMS, and organization websites in a single unified API."
        canonical={canonicalUrl("/connectivity")}
      />
      <FieldHeader />

      <div className="col">
        <div className="masthead">
          <div className="eyebrow">Connectivity Intelligence</div>
          <h1>How do I connect to this provider?</h1>
          <p className="lede">A unified view of healthcare integration endpoints — discovered from public directories, verified, and kept current.</p>
        </div>

        <section>
          <h2>What it is</h2>
          <p>
            FHIRfly Connectivity Intelligence answers the question developers keep hitting:{" "}
            <em>"How do I actually connect to this provider or organization?"</em>
          </p>
          <p>
            We aggregate publicly available endpoint information from sources like the NPPES Endpoint
            Reference File, the CMS National Provider Directory, and organization websites into a
            single, consistent view of healthcare connectivity options.
          </p>
        </section>

        <section>
          <h2>How we verify endpoints</h2>
          <p>To keep the data accurate, we periodically confirm that published endpoints are reachable. Our verification:</p>
          <ul>
            <li>Only checks publicly listed FHIR endpoints</li>
            <li>Makes minimal, read-only requests (SMART discovery, capability statements)</li>
            <li>Respects rate limits (max 10 requests per domain per hour)</li>
            <li>Uses a transparent User-Agent identifying our service</li>
            <li>Never attempts authentication or accesses protected data</li>
          </ul>
        </section>

        <section>
          <h2>Identifying our requests</h2>
          <p>Our verification requests use this User-Agent:</p>
          <code className="ua">FHIRfly-Verifier/1.0 (+https://fhirfly.io/connectivity; connectivity@fhirfly.io)</code>
          <p>If you see it in your server logs, we're verifying endpoint information that was publicly listed in a healthcare directory.</p>
        </section>

        <section>
          <h2>Questions, updates, or opt-out</h2>
          <p>
            To ask about our verification process, update your endpoint information, or opt out, email{" "}
            <a href="mailto:connectivity@fhirfly.io">connectivity@fhirfly.io</a>. You can also block our
            User-Agent in your server configuration if you'd prefer not to receive verification requests.
          </p>
        </section>

        <div className="callout">
          <div className="k">For developers</div>
          <h2>The Connectivity API</h2>
          <p>Currently in development. When it launches you'll be able to:</p>
          <ul>
            <li>Look up connectivity options for any NPI</li>
            <li>Discover FHIR, Direct, and HL7v2 endpoints</li>
            <li>See verification status and last-checked timestamps</li>
            <li>Understand organizational relationships</li>
          </ul>
          <Link className="more" to="/docs">View the API docs →</Link>
        </div>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
